import {noop} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import useValidateProcedureById, {
  ProcedureValidationErrorType,
} from 'procedures/shared-components/validation/useValidateProcedureById';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import ProcedureRunDesignFlowchartEditor from '../../flowcharts/design/ProcedureRunDesignFlowchartEditor';
import ProcedureSearchSidepanel, {
  PROCEDURE_SELECTION_DND_MIME_TYPE,
  SelectedProcedure,
} from '../procedure-selection/ProcedureSearchSidepanel';
import {studyDesignTabStyles} from './StudyDesignTabSharedComponents';

/**
 * Renders the editor for a procedure run plan that has no linked flowchart version yet
 * (procedure-less). Reuses the real flowchart editor shell with empty methods/conditions so the
 * empty state matches the populated editor (same sidebar, panels, and canvas background). Dragging
 * a procedure card from the search sidepanel onto the empty canvas attaches its flowchart version
 * to the existing plan and refetches.
 *
 */
export default function EmptyProcedureRunPlanDesignTabContents({
  procedureRunPlanId,
  studyId,
  studyType,
  procedureType,
  refetch,
}: {
  procedureRunPlanId: string;
  studyId: string;
  studyType: StudyType;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const [isDropTargetActive, setIsDropTargetActive] = useState(false);
  const [stagedProcedure, setStagedProcedure] = useState<SelectedProcedure | null>(null);
  const validationResult = useValidateProcedureById(stagedProcedure?.procedureId ?? null);
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureSentenceCaseString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
  });

  function parseDroppedProcedure(event: React.DragEvent<HTMLDivElement>): SelectedProcedure | null {
    const dndPayload = event.dataTransfer.getData(PROCEDURE_SELECTION_DND_MIME_TYPE);
    if (dndPayload.length === 0) {
      return null;
    }
    let parsed: Partial<SelectedProcedure>;
    try {
      parsed = JSON.parse(dndPayload) as Partial<SelectedProcedure>;
    } catch (err) {
      // Return null if the user tries to drop something without a valid JSON payload
      if (err instanceof SyntaxError) {
        return null;
      }
      throw err;
    }
    // Return null if the user tries to drop something other than the procedure card
    if (
      typeof parsed.procedureId !== 'string' ||
      typeof parsed.workflowFlowchartConfigVersionId !== 'string'
    ) {
      return null;
    }
    return {
      procedureId: parsed.procedureId,
      workflowFlowchartConfigVersionId: parsed.workflowFlowchartConfigVersionId,
    };
  }

  const handleSelectProcedure = useCallback(
    async ({workflowFlowchartConfigVersionId}: SelectedProcedure): Promise<void> => {
      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlanId,
          studyId,
          workflowFlowchartConfigVersionId,
        }),
        `Adding ${procedureString} to plan...`
      );
      refetch();
    },
    [procedureRunPlanId, studyId, procedureString, refetch]
  );

  useEffect(() => {
    if (stagedProcedure == null || validationResult == null) {
      return;
    }
    if (
      validationResult.isValid === false &&
      validationResult.errorType === ProcedureValidationErrorType.NOT_LOADED
    ) {
      return;
    }
    if (validationResult.isValid) {
      const selectedProcedure = stagedProcedure;
      setStagedProcedure(null);
      handleSelectProcedure(selectedProcedure).catch((error: unknown) => {
        if (net.isServerRequestError(error)) {
          notifications.error(
            <ToastMessage
              header={`Failed to add ${procedureString} to plan`}
              body={notifications.getFriendlyErrorAsElement(error as Response, '')}
            />
          );
          return;
        }
        throw error;
      });
      return;
    }
    setStagedProcedure(null);
    notifications.error(
      <ToastMessage
        header={`Failed to add ${procedureString} to plan`}
        body={`${procedureSentenceCaseString} contains errors. Fix errors to run the ${procedureString}.`}
      />
    );
  }, [
    stagedProcedure,
    validationResult,
    procedureString,
    procedureSentenceCaseString,
    handleSelectProcedure,
  ]);

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'EmptyProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      <ProcedureRunDesignFlowchartEditor
        conditions={[]}
        methods={[]}
        activeMethodId={null}
        procedureType={procedureType}
        selectedMethodId={null}
        onChangeSelectedMethod={noop}
        // Force the left panel open so the procedure search sidepanel is visible immediately.
        isInEditState={true}
        allowEditingPersistedConditions={true}
        edgeSpecs={[]}
        leftPanelChildren={<ProcedureSearchSidepanel procedureType={procedureType} studyType={studyType} />}
        rightPanelChildren={null}
        canvasDnd={{
          isDropTargetActive,
          onDragEnter: (event) => {
            event.preventDefault();
            if (!event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
              return;
            }
            setIsDropTargetActive(true);
          },
          onDragOver: (event) => {
            event.preventDefault();
            if (event.dataTransfer.types.includes(PROCEDURE_SELECTION_DND_MIME_TYPE)) {
              event.dataTransfer.dropEffect = 'copy';
            }
          },
          onDragLeave: (event) => {
            event.preventDefault();
            setIsDropTargetActive(false);
          },
          onDrop: (event) => {
            event.preventDefault();
            setIsDropTargetActive(false);
            const selectedProcedure = parseDroppedProcedure(event);
            if (selectedProcedure == null) {
              return;
            }
            setStagedProcedure(selectedProcedure);
          },
        }}
      />
    </div>
  );
}
